import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  ChevronDown,
  Clock,
  GraduationCap,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value || 0);
const marketplaceImage = (value) =>
  !value || /^https?:\/\//.test(value)
    ? value
    : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${value.startsWith("/") ? "" : "/"}${value}`;
const ordered = (rows, slugs) => {
  const positions = new Map((slugs || []).map((slug, index) => [slug, index]));
  return [...rows].sort((a, b) => {
    const ai = positions.has(a.slug) ? positions.get(a.slug) : 9999;
    const bi = positions.has(b.slug) ? positions.get(b.slug) : 9999;
    if (ai !== bi) return ai - bi;
    return Number(Boolean(b.featured)) - Number(Boolean(a.featured));
  });
};

const PublishedCatalogs = ({ config: provided }) => {
  const { lang } = useLanguage();
  const [diagnostics, setDiagnostics] = useState([]);
  const [courses, setCourses] = useState([]);
  const [products, setProducts] = useState([]);
  const [expandedSlug, setExpandedSlug] = useState(null);
  const [config, setConfig] = useState(provided || null);
  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/api/diagnostics/public/catalog", {
        params: { landing_only: true },
      }),
      api.get("/api/academy/public/courses", {
        params: { landing_only: true },
      }),
      api.get("/api/marketplace/landing-products"),
      provided
        ? Promise.resolve({ data: provided })
        : api.get("/api/landing/public"),
    ])
      .then(([d, c, m, l]) => {
        if (active) {
          setDiagnostics(d.data);
          setCourses(c.data);
          setProducts(m.data);
          setConfig(l.data);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [provided]);
  const view = config?.content?.[lang] || config?.content?.es || {};
  const display = useMemo(() => config?.display || {}, [config]);
  const sections = config?.sections || {};
  const shownDiagnostics = useMemo(
    () =>
      ordered(diagnostics, display.diagnostic_order).slice(
        0,
        display.diagnostic_limit ?? 6,
      ),
    [diagnostics, display],
  );
  const shownCourses = useMemo(
    () =>
      ordered(courses, display.course_order).slice(
        0,
        display.course_limit ?? 3,
      ),
    [courses, display],
  );
  const shownProducts = useMemo(
    () => products.slice(0, display.marketplace_limit ?? 6),
    [products, display],
  );
  return (
    <>
      {sections.published_diagnostics !== false && (
        <section
          className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20"
          data-testid="landing-published-diagnostics"
        >
          <h2 className="font-cinzel kd9-gold-text text-2xl md:text-4xl tracking-[0.18em] text-center mb-12">
            {view.diagnostics_title || "DIAGNÓSTICOS PUBLICADOS"}
          </h2>
          {shownDiagnostics.length === 0 ? (
            <div
              className="kd9-card p-8 text-center font-cormorant text-xl"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              {view.diagnostics_empty || "Nuevos diagnósticos próximamente."}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {shownDiagnostics.map((item, i) => {
                const expanded = expandedSlug === item.slug;
                const panelId = `landing-diagnostic-${item.slug}`;
                return (
                  <article
                    key={item.slug}
                    className={`kd9-card overflow-hidden kd9-fade-up kd9-delay-${(i % 6) + 1}`}
                    data-testid={`landing-diagnostic-card-${item.slug}`}
                  >
                    <button
                      type="button"
                      className="relative block w-full text-left group"
                      onClick={() =>
                        setExpandedSlug(expanded ? null : item.slug)
                      }
                      aria-expanded={expanded}
                      aria-controls={panelId}
                    >
                      <div className="relative h-56 sm:h-64 lg:h-72">
                        {item.coverImageUrl || item.cover_url ? (
                          <img
                            src={item.coverImageUrl || item.cover_url}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Sparkles
                              size={58}
                              style={{ color: item.tone || "#D4AF37" }}
                            />
                          </div>
                        )}
                        <div
                          className="absolute inset-0"
                          style={{
                            background:
                              "linear-gradient(180deg,rgba(0,0,0,.08),rgba(0,0,0,.9))",
                          }}
                        />
                        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 flex items-end justify-between gap-5">
                          <h3
                            className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-bold tracking-wide"
                            style={{ color: "var(--kd9-gold-2)" }}
                          >
                            {item.title}
                          </h3>
                          <span
                            className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-transform duration-300"
                            style={{
                              background: "rgba(5,5,5,.72)",
                              border: "1px solid var(--kd9-border)",
                              transform: expanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            }}
                            aria-hidden="true"
                          >
                            <ChevronDown
                              size={24}
                              style={{ color: "var(--kd9-gold)" }}
                            />
                          </span>
                        </div>
                      </div>
                    </button>
                    <div
                      id={panelId}
                      className="overflow-hidden transition-all duration-500 ease-in-out"
                      style={{
                        maxHeight: expanded ? "1200px" : "0px",
                        opacity: expanded ? 1 : 0,
                      }}
                      aria-hidden={!expanded}
                    >
                      <div
                        className="p-6 sm:p-8 border-t"
                        style={{ borderColor: "var(--kd9-border)" }}
                      >
                        <p
                          className="font-cormorant text-lg sm:text-xl leading-relaxed mb-6 whitespace-pre-line"
                          style={{ color: "var(--kd9-text-soft)" }}
                        >
                          {item.description}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-5 mb-7">
                          <div
                            className="rounded-xl p-4"
                            style={{
                              background: "rgba(212,175,55,.06)",
                              border: "1px solid var(--kd9-border)",
                            }}
                          >
                            <div className="font-mont text-[10px] uppercase tracking-[.2em] mb-2">
                              {lang === "en" ? "Energy cost" : "Costo en energía"}
                            </div>
                            <div className="font-cinzel kd9-gold-text text-2xl">
                              {new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(item.energy_cost || 0)} Energía KD9
                            </div>
                          </div>
                          <div
                            className="rounded-xl p-4"
                            style={{
                              background: "rgba(212,175,55,.06)",
                              border: "1px solid var(--kd9-border)",
                            }}
                          >
                            <div className="font-mont text-[10px] uppercase tracking-[.2em] mb-2">
                              {lang === "en" ? "Category" : "Categoría"}
                            </div>
                            <div
                              className="font-cormorant text-xl"
                              style={{ color: "var(--kd9-text-soft)" }}
                            >
                              {item.category || "KD9"}
                            </div>
                          </div>
                        </div>
                        {item.requirements?.length > 0 && (
                          <div className="mb-7">
                            <div className="font-mont text-[10px] uppercase tracking-[.2em] mb-3">
                              {lang === "en" ? "Requirements" : "Requisitos"}
                            </div>
                            <ul
                              className="font-cormorant text-lg list-disc pl-5 space-y-1"
                              style={{ color: "var(--kd9-text-soft)" }}
                            >
                              {item.requirements.map((requirement, index) => (
                                <li key={`${item.slug}-requirement-${index}`}>
                                  {requirement}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <Link
                          to="/register"
                          className="kd9-btn-primary px-6 py-3 rounded-lg text-[11px] inline-flex gap-2 items-center"
                        >
                          {lang === "en"
                            ? "Request diagnostic"
                            : "Solicitar diagnóstico"}{" "}
                          <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}
      {sections.published_courses !== false && shownCourses.length > 0 && (
        <section
          className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16"
          data-testid="landing-published-courses"
        >
          <h2 className="font-cinzel kd9-gold-text text-2xl md:text-4xl tracking-[0.18em] text-center mb-12">
            {view.courses_title || "ACADEMIA KD9"}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {shownCourses.map((course) => (
              <article key={course.slug} className="kd9-card p-6">
                <GraduationCap
                  size={25}
                  style={{ color: "var(--kd9-gold)" }}
                  className="mb-4"
                />
                <h3 className="font-cinzel kd9-gold-text text-lg mb-2">
                  {course.title}
                </h3>
                <p
                  className="font-cormorant mb-3"
                  style={{ color: "var(--kd9-text-soft)" }}
                >
                  {course.description}
                </p>
                <div
                  className="flex gap-3 text-[11px] font-mont mb-3"
                  style={{ color: "var(--kd9-text-soft)" }}
                >
                  <span>
                    <Clock size={12} className="inline" />{" "}
                    {course.duration ||
                      (lang === "en" ? "At your pace" : "A tu ritmo")}
                  </span>
                  <span>
                    <BookOpen size={12} className="inline" />{" "}
                    {course.lessons_count}
                  </span>
                </div>
                <div className="font-cinzel kd9-gold-text mb-4">
                  {course.price
                    ? money(course.price)
                    : lang === "en"
                      ? "Free"
                      : "Gratis"}
                </div>
                <Link
                  to="/academia"
                  className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] inline-flex gap-2 items-center"
                >
                  {lang === "en" ? "View course" : "Ver curso"}{" "}
                  <ArrowRight size={12} />
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}
      {shownProducts.length > 0 && (
        <section
          className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16"
          data-testid="landing-marketplace-products"
        >
          <h2 className="font-cinzel kd9-gold-text text-2xl md:text-4xl tracking-[0.18em] text-center mb-12">
            {lang === "en" ? "KD9 MARKETPLACE" : "MARKETPLACE KD9"}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shownProducts.map((product) => (
              <article key={product.slug} className="kd9-card overflow-hidden">
                <div className="h-48 flex items-center justify-center bg-black/20">
                  {product.images?.[0] ? (
                    <img
                      src={marketplaceImage(product.images[0])}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ShoppingBag
                      size={42}
                      style={{ color: "var(--kd9-gold)" }}
                    />
                  )}
                </div>
                <div className="p-5">
                  <div className="font-mont text-[10px] uppercase kd9-gold-text">
                    {product.category}
                  </div>
                  <h3 className="font-cinzel text-xl my-2">{product.name}</h3>
                  <p
                    className="font-cormorant line-clamp-3"
                    style={{ color: "var(--kd9-text-soft)" }}
                  >
                    {product.description}
                  </p>
                  <div className="font-cinzel kd9-gold-text text-xl my-4">
                    {`⚡ ${Number(product.energy_cost || 0).toLocaleString("es-CO")} Energía KD9`}
                  </div>
                  <Link
                    to="/marketplace"
                    className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] inline-flex gap-2 items-center"
                  >
                    {lang === "en" ? "View product" : "Ver producto"}{" "}
                    <ArrowRight size={12} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </>
  );
};
export default PublishedCatalogs;
